import type { AuthoredGridPose } from '../schema/index.ts';
import { FIXED_UNITS_PER_TILE, type AuthoredStationGridDelta, type FixedPoint } from './types.ts';

export const AUTHORED_HEADING_FORWARD: readonly FixedPoint[] = [
  { x: 1, y: 0, z: 0 },
  { x: 1, y: 0, z: 1 },
  { x: 0, y: 0, z: 1 },
  { x: -1, y: 0, z: 1 },
  { x: -1, y: 0, z: 0 },
  { x: -1, y: 0, z: -1 },
  { x: 0, y: 0, z: -1 },
  { x: 1, y: 0, z: -1 },
] as const;

export const AUTHORED_HEADING_LABELS = [
  'East',
  'Southeast',
  'South',
  'Southwest',
  'West',
  'Northwest',
  'North',
  'Northeast',
] as const;

export function normalizeAuthoredHeadingStep(step: number): number {
  return ((step % 8) + 8) % 8;
}

export function authoredHeadingLabel(step: number): string {
  return AUTHORED_HEADING_LABELS[normalizeAuthoredHeadingStep(step)]!;
}

export function authoredForward(step: number): FixedPoint {
  return AUTHORED_HEADING_FORWARD[normalizeAuthoredHeadingStep(step)]!;
}

export function authoredRight(step: number): FixedPoint {
  const forward = authoredForward(step);
  return { x: -forward.z, y: 0, z: forward.x };
}

export function signedAuthoredHeadingSteps(from: number, to: number): number {
  const clockwise = normalizeAuthoredHeadingStep(to - from);
  return clockwise > 4 ? clockwise - 8 : clockwise;
}

export function authoredStationGridDelta(
  entry: AuthoredGridPose,
  target: AuthoredGridPose,
): AuthoredStationGridDelta {
  const forward = authoredForward(entry.headingStep);
  const right = authoredRight(entry.headingStep);
  const dx = target.x - entry.x;
  const dz = target.z - entry.z;
  const denominator = forward.x ** 2 + forward.z ** 2;
  const forwardUnits = (dx * forward.x + dz * forward.z) / denominator;
  const rightUnits = (dx * right.x + dz * right.z) / denominator;
  return {
    forwardTiles: forwardUnits / FIXED_UNITS_PER_TILE,
    rightTiles: rightUnits / FIXED_UNITS_PER_TILE,
    elevationLevels: (target.y - entry.y) / FIXED_UNITS_PER_TILE,
    requiredHeadingStep: normalizeAuthoredHeadingStep(target.headingStep),
    signedHeadingChangeSteps: signedAuthoredHeadingSteps(entry.headingStep, target.headingStep),
  };
}
